import { rub, ZERO, type Money } from './money.js';
import { pd, type PlainDate } from './date.js';
import type { AvailableInput } from './available.js';
import type {
  Account, CreditTerms, Goal, IncomeStream, Loan, Obligation, PlannedExpense,
} from './types.js';

/**
 * Эталонная фикстура — тот же набор, что в дизайн-брифе §16.
 * Любое изменение движков сверяется с ней: разбор обязан дать ровно 42 300 ₽,
 * а темп — ровно 2 350 ₽ в день. Числа подобраны так, чтобы сходились нацело,
 * иначе несходящаяся арифметика расползётся по макетам и фикстурам.
 */
/**
 * Сегодня в демо — понедельник 7 сентября 2026.
 * Дата выбрана не произвольно: зарплата 27-го числа, но 27 сентября 2026 —
 * воскресенье, поэтому по правилу переноса она приходит в пятницу 25-го.
 * От 7-го до 25-го ровно 18 дней, и 42 300 ₽ делятся на 18 нацело — по 2 350 ₽.
 * Обе цифры на главной («18 дней» и «≈ 2 350 ₽ в день») сходятся только так.
 */
export const DEMO_TODAY: PlainDate = pd('2026-09-07');

export const demoAccounts: Account[] = [
  { id: 'acc_main', name: 'Основная карта', kind: 'debit', balance: rub(137_420), isLiquid: true, includeInNetWorth: true },
  { id: 'acc_cash', name: 'Наличные', kind: 'cash', balance: rub(23_500), isLiquid: true, includeInNetWorth: true },
  { id: 'acc_save', name: 'Накопительный счёт', kind: 'savings', balance: rub(320_000), isLiquid: false, includeInNetWorth: true },
  { id: 'acc_cc', name: 'Кредитная карта', kind: 'credit_card', balance: rub(-152_400), isLiquid: false, includeInNetWorth: true },
  { id: 'acc_broker', name: 'Брокерский счёт', kind: 'brokerage', balance: rub(204_000), isLiquid: false, includeInNetWorth: true },
  { id: 'acc_kid', name: 'Счёт ребёнка', kind: 'custodial', balance: rub(80_000), isLiquid: false, includeInNetWorth: true },
];

export const demoCreditTerms: CreditTerms[] = [
  {
    accountId: 'acc_cc',
    limit: rub(300_000),
    statementDebt: rub(152_400),
    reserved: rub(134_400),
    statementDay: 1,
    paymentDay: 10,
    graceDays: 100,
    minPaymentPct: 0.05,
    minPaymentFloor: rub(15_000),
  },
];

/**
 * Долги эталона: дорогая карта и ипотека под умеренный процент.
 *
 * Ипотека ссылается на своё обязательство: платёж попадает в план одним
 * путём, а кредит добавляет к нему остаток, ставку и срок.
 */
export const demoLoans: Loan[] = [
  {
    id: 'loan_cc', name: 'Кредитная карта', kind: 'credit_card',
    principal: rub(152_400), ratePct: 39, payment: rub(15_000), paymentDay: 10,
    accountId: 'acc_cc', allowsEarly: true,
    card: {
      accountId: 'acc_cc', limit: rub(300_000), statementDay: 1,
      statementDebt: rub(152_400), graceDays: 100, reserved: rub(134_400),
      minPaymentPct: 0.05, minPaymentFloor: rub(15_000),
    },
  },
  {
    id: 'loan_mortgage', name: 'Ипотека', kind: 'amortizing',
    principal: rub(4_200_000), ratePct: 12, payment: rub(36_000), paymentDay: 12,
    obligationId: 'ob_mortgage', accountId: 'acc_main', allowsEarly: true,
  },
];

export const demoIncome: IncomeStream[] = [
  { id: 'inc_main', name: 'Основная работа', accountId: 'acc_main', amount: rub(260_000), isVariable: false, dayOfMonth: 27, shiftRule: 'previous_business_day', isActive: true },
  { id: 'inc_side', name: 'Подработка', accountId: 'acc_main', amount: rub(40_000), isVariable: true, dayOfMonth: 27, shiftRule: 'previous_business_day', isActive: true },
];

export const demoObligations: Obligation[] = [
  { id: 'ob_mortgage', name: 'Ипотека', kind: 'loan', amountType: 'fixed', amount: rub(36_000), dayOfMonth: 12, shiftRule: 'exact', accountId: 'acc_main', part: 'need', isActive: true },
  { id: 'ob_kinder', name: 'Детский сад', kind: 'service', amountType: 'fixed', amount: rub(9_400), dayOfMonth: 15, shiftRule: 'exact', accountId: 'acc_main', part: 'need', isActive: true },
  { id: 'ob_subs', name: 'Подписки', kind: 'subscription', amountType: 'fixed', amount: rub(2_400), dayOfMonth: 14, shiftRule: 'exact', accountId: 'acc_main', part: 'need', isActive: true },
  { id: 'ob_phone', name: 'Связь', kind: 'utility', amountType: 'fixed', amount: rub(1_200), dayOfMonth: 20, shiftRule: 'exact', accountId: 'acc_main', part: 'need', isActive: true },
  { id: 'ob_misc', name: 'Прочие обязательства', kind: 'other', amountType: 'variable', amount: rub(1_620), amountEstimate: rub(1_620), dayOfMonth: 18, shiftRule: 'exact', accountId: 'acc_main', part: 'need', isActive: true },
];

export const demoGoals: Goal[] = [
  { id: 'goal_debt', name: 'Закрыть кредитку', kind: 'debt_payoff', targetAmount: rub(152_400), currentAmount: ZERO, loanId: 'loan_cc', priority: 'critical', monthlyContribution: ZERO, contributionMode: 'residual', fundedInPeriod: false, status: 'active' },
  { id: 'goal_buffer', name: 'Подушка', kind: 'emergency_fund', targetAmount: rub(450_000), currentAmount: rub(220_000), targetDate: pd('2027-05-01'), priority: 'high', monthlyContribution: rub(20_000), contributionMode: 'fixed', fundedInPeriod: false, status: 'active' },
  { id: 'goal_wardrobe', name: 'Шкаф в прихожую', kind: 'purchase', targetAmount: rub(140_000), currentAmount: rub(25_000), targetDate: pd('2026-12-01'), priority: 'medium', monthlyContribution: rub(30_000), contributionMode: 'fixed', fundedInPeriod: false, status: 'active' },
  { id: 'goal_vacation', name: 'Отпуск', kind: 'purchase', targetAmount: rub(300_000), currentAmount: rub(120_000), targetDate: pd('2027-07-01'), priority: 'medium', monthlyContribution: ZERO, contributionMode: 'residual', fundedInPeriod: false, status: 'active' },
];

export const demoPlanned: PlannedExpense[] = [];

export const demoInput = (): AvailableInput => ({
  today: DEMO_TODAY,
  accounts: demoAccounts.map((a) => ({ ...a })),
  creditTerms: demoCreditTerms.map((t) => ({ ...t })),
  incomeStreams: demoIncome.map((s) => ({ ...s })),
  obligations: demoObligations.map((o) => ({ ...o })),
  goals: demoGoals.map((g) => ({ ...g })),
  plannedExpenses: demoPlanned.map((p) => ({ ...p })),
  loans: demoLoans.map((l) => ({ ...l })),
});

/** Ожидаемые значения эталона — если они меняются, это осознанное решение. */
export const DEMO_EXPECTED = {
  liquid: rub(160_920) as Money,
  obligations: rub(50_620) as Money,
  credit: rub(18_000) as Money,
  goals: rub(50_000) as Money,
  available: rub(42_300) as Money,
  perDay: rub(2_350) as Money,
  daysToHorizon: 18,
  horizonDate: pd('2026-09-25'),
} as const;

/** Медианы обычных трат домохозяйства из эталона. */
export const DEMO_NEED_BASELINE = rub(82_000);
export const DEMO_WANT_BASELINE = rub(20_000);
